import { useRef, useEffect, useCallback } from "react";

interface TimeWheelPickerProps {
  value: { hour: number; minute: number };
  onChange: (time: { hour: number; minute: number }) => void;
}

const ITEM_HEIGHT = 48;
const VISIBLE_ITEMS = 5; // odd number so centre item is selected

/* ─── Helpers ─────────────────────────────────────────── */
export function nearestQuarter(date: Date): { hour: number; minute: number } {
  const totalMinutes = date.getHours() * 60 + date.getMinutes();
  const rounded = Math.round(totalMinutes / 15) * 15;
  return { hour: Math.floor(rounded / 60) % 24, minute: rounded % 60 };
}

/* ─── Single Wheel Column ──────────────────────────────── */
interface WheelColProps {
  items: (string | number)[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  formatItem?: (item: string | number) => string;
  width?: number;
}

export const WheelCol = ({ items, selectedIndex, onSelect, formatItem, width = 60 }: WheelColProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging    = useRef(false);
  const startY        = useRef(0);
  const startScroll   = useRef(0);
  const snapTimer     = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* Scroll to selectedIndex whenever it changes externally */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const target = selectedIndex * ITEM_HEIGHT;
    if (Math.abs(el.scrollTop - target) > 2) {
      el.scrollTo({ top: target, behavior: "smooth" });
    }
  }, [selectedIndex]);

  const snapToNearest = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const index = Math.max(0, Math.min(items.length - 1, Math.round(el.scrollTop / ITEM_HEIGHT)));
    el.scrollTo({ top: index * ITEM_HEIGHT, behavior: "smooth" });
    onSelect(index);
  }, [items.length, onSelect]);

  /* Native scroll (mouse wheel / touch fling) */
  const handleScroll = useCallback(() => {
    if (isDragging.current) return;
    if (snapTimer.current) clearTimeout(snapTimer.current);
    snapTimer.current = setTimeout(snapToNearest, 120);
  }, [snapToNearest]);

  /* Pointer drag (mouse click-drag + touch) */
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    const el = containerRef.current;
    if (!el) return;
    isDragging.current = true;
    startY.current     = e.clientY;
    startScroll.current = el.scrollTop;
    el.setPointerCapture(e.pointerId);
    e.preventDefault();
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging.current) return;
    const el = containerRef.current;
    if (!el) return;
    const delta = startY.current - e.clientY;
    el.scrollTop = startScroll.current + delta;
    e.preventDefault();
  }, []);

  const handlePointerUp = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;
    snapToNearest();
  }, [snapToNearest]);

  const totalHeight = ITEM_HEIGHT * VISIBLE_ITEMS;
  const padding     = ITEM_HEIGHT * Math.floor(VISIBLE_ITEMS / 2);

  return (
    <div style={{ position: "relative", width, height: totalHeight, flexShrink: 0 }}>
      {/* Selection highlight bar */}
      <div style={{
        position: "absolute",
        top: padding,
        left: 0,
        right: 0,
        height: ITEM_HEIGHT,
        background: "rgba(139, 92, 246, 0.12)",
        borderRadius: 10,
        pointerEvents: "none",
        zIndex: 1,
      }} />

      {/* Scrollable track */}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        style={{
          width: "100%",
          height: "100%",
          overflowY: "scroll",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          cursor: "grab",
          userSelect: "none",
          touchAction: "none", // hand pointer events ourselves
        }}
      >
        {/* Top spacer */}
        <div style={{ height: padding }} />

        {items.map((item, i) => {
          const distance = Math.abs(i - selectedIndex);
          const isSelected = i === selectedIndex;
          return (
            <div
              key={i}
              onClick={() => {
                onSelect(i);
                containerRef.current?.scrollTo({ top: i * ITEM_HEIGHT, behavior: "smooth" });
              }}
              style={{
                height: ITEM_HEIGHT,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: isSelected ? 22 : distance === 1 ? 18 : 15,
                fontWeight: isSelected ? 700 : 400,
                color: isSelected ? "#1f2937" : distance === 1 ? "#6b7280" : "#d1d5db",
                transition: "font-size 0.15s, color 0.15s, font-weight 0.15s",
                cursor: "pointer",
              }}
            >
              {formatItem ? formatItem(item) : item}
            </div>
          );
        })}

        {/* Bottom spacer */}
        <div style={{ height: padding }} />
      </div>
    </div>
  );
};

/* ─── TimeWheelPicker ──────────────────────────────────── */
const TimeWheelPicker = ({ value, onChange }: TimeWheelPickerProps) => {
  const hours   = Array.from({ length: 12 }, (_, i) => i + 1); // 1–12
  const minutes = [0, 15, 30, 45];
  const periods = ["AM", "PM"];

  const displayHour  = value.hour % 12 || 12;
  const displayMin   = Math.round(value.minute / 15) * 15 % 60;
  const displayPer   = value.hour >= 12 ? "PM" : "AM";

  const hourIndex   = hours.indexOf(displayHour);
  const minuteIndex = minutes.indexOf(displayMin) >= 0 ? minutes.indexOf(displayMin) : 0;
  const periodIndex = displayPer === "AM" ? 0 : 1;

  const handleHourSelect = useCallback((idx: number) => {
    const h = hours[idx];
    const hour24 = displayPer === "PM" ? (h === 12 ? 12 : h + 12) : (h === 12 ? 0 : h);
    onChange({ hour: hour24, minute: value.minute });
  }, [hours, displayPer, onChange, value.minute]);

  const handleMinuteSelect = useCallback((idx: number) => {
    onChange({ hour: value.hour, minute: minutes[idx] });
  }, [minutes, onChange, value.hour]);

  const handlePeriodSelect = useCallback((idx: number) => {
    const per = periods[idx] as "AM" | "PM";
    const hour24 = per === "PM"
      ? (displayHour === 12 ? 12 : displayHour + 12)
      : (displayHour === 12 ? 0 : displayHour);
    onChange({ hour: hour24, minute: value.minute });
  }, [periods, displayHour, onChange, value.minute]);

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
      <WheelCol
        items={hours}
        selectedIndex={hourIndex}
        onSelect={handleHourSelect}
        width={60}
      />

      <span style={{ fontSize: 26, fontWeight: 700, color: "#9ca3af", paddingBottom: 2, flexShrink: 0 }}>:</span>

      <WheelCol
        items={minutes}
        selectedIndex={minuteIndex}
        onSelect={handleMinuteSelect}
        formatItem={(m) => String(m).padStart(2, "0")}
        width={60}
      />

      <WheelCol
        items={periods}
        selectedIndex={periodIndex}
        onSelect={handlePeriodSelect}
        width={56}
      />
    </div>
  );
};

export default TimeWheelPicker;
